import React from "react";
import { useTranslation } from "next-i18next";
import Styles from "./PartLoadBooking.module.scss";
import {
    usePartLoadFeatures,
    useOlbHowItworkData,
    useFtlAchievement,
    useCustomerSaysData,
    usePlbServiceFeature,
    usePlTPros,
    useFlTPros
} from "../service-data/ServiceData";

import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";
import { PLATFORM_STATS, getPlatformStatI18nLabels } from "@vahak/core/dist/constants/platformStats";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";

import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { GA4EventNames, GA4ServicePagePartTruckLoadEventValues } from "@vahak/core/dist/constants/event-names";
import Intro from "../common/intro/Intro";
import OurCustomerSays from "../common/our-customer-says/OurCustomerSays";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";
import GenericTitleDescriptionWithParas from "../../generic-title-description-with-paras/GenericTitleDescriptionWithParas";
import Typography from "@vahak/core-ui/dist/components/Typography/Typography";
import Flex from "@vahak/core-ui/dist/layout/Flex";

interface PartLoadBookingProps {
    faqs: FaqsResponse[];
}
const metaData = {
    title: "Book Part Truck Load Services Online in India | Vahak",
    description: `Optimize your trucks for maximum load capacity through part load booking. Choose from ${PLATFORM_STATS.liveLoadsAndLorries.label} truck options to transport your goods anywhere in India hassle-free.`,
    keywords:
        "part load booking, part load, part load transport, part load transport service, part load transport service near me, part load transport service in delhi, part load transport service in mumbai, part load transport service in india, part truck load services, full load and part load truck transport services, part load delivery, part load logistics, part load transport service in bangalore, ",
    canonicalUrl: "https://www.vahak.in/part-load-booking",
    schemaOrgJsonUrl: "/part-load-booking",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1920/75/webp/truck_driver_with_loads.png",
    ogImgAltText: "Book Part Truck Load",
    breadcrumbList: breadcrumbsConstants({}).partTruckLoad
};

const PartLoadBooking = ({ faqs }: PartLoadBookingProps) => {
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />
            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:part-load-booking.hero-section.bannerTitle")}
                    bannerText={t("servicePages:part-load-booking.hero-section.bannerText")}
                    hrefURL={`${ROUTES.marketplace.getUrl("load")}?${SOURCE_UTM}=${PageTypes.PART_LOAD_BOOKING}`}
                    hrefLabel={t("servicePages:part-load-booking.hero-section.hrefLabel")}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_part_truck_load, {
                            value: GA4ServicePagePartTruckLoadEventValues.book_part_load
                        });
                    }}
                    // srcSetURL={`${IMG_BASE_URL}/truck_driver_with_loads_3x.png 1956w, ${IMG_BASE_URL}/truck_driver_with_loads_2x.png 1304w, ${IMG_BASE_URL}/truck_driver_with_loads.png 652w, ${IMG_BASE_URL}/truck_driver_with_loads_mobile_3x.png 694w, ${IMG_BASE_URL}/truck_driver_with_loads_mobile_2x.png 463w, ${IMG_BASE_URL}/truck_driver_with_loads_mobile.png 232w`}
                    srcURL={`truck_driver_with_loads.png`}
                    altText="truck driver with loads"
                    sectionClass={Styles.herroSection}
                />

                <Achievements
                    achievementData={useFtlAchievement()}
                    sectionTitle={t("servicePages:part-load-booking.achievements.sectionTitle")}
                    sectionText={t(
                        "servicePages:part-load-booking.achievements.sectionText",
                        getPlatformStatI18nLabels()
                    )}
                />

                <Intro text={t("servicePages:part-load-booking.intro.text")} />
                <ServiceInformation
                    heading={t("servicePages:part-load-booking.benefits-of-booking-a-part-load-truck.heading")}
                    sectionTitle=""
                    sectionText={""}
                    descriptionComponent={
                        <>
                            <GenericTitleDescriptionWithParas
                                titleDescriptionList={usePlbServiceFeature()}
                                heading={""}
                            />
                        </>
                    }
                />
                <ServiceInformation
                    heading={t(
                        "servicePages:part-load-booking.best-trucks-suited-for-part-truck-load-transport.heading"
                    )}
                    sectionTitle={""}
                    sectionText={t(
                        "servicePages:part-load-booking.best-trucks-suited-for-part-truck-load-transport.description"
                    )}
                />
                <ServiceInformation
                    heading={t(
                        "servicePages:part-load-booking.part-truck-load-transport-service-providers-in-india.heading"
                    )}
                    sectionTitle={""}
                    sectionText={t(
                        "servicePages:part-load-booking.part-truck-load-transport-service-providers-in-india.description"
                    )}
                />

                <ServiceInformation
                    heading={t(
                        "servicePages:part-load-booking.difference-between-part-load-and-full-load-transport.heading"
                    )}
                    sectionTitle=""
                    sectionText={t(
                        "servicePages:part-load-booking.difference-between-part-load-and-full-load-transport.description"
                    )}
                    descriptionComponent={
                        <Flex gap={50} margin={"32px 0"} flexWrap="wrap" flexDirection="row">
                            <div>
                                <Typography as="p">
                                    {t(
                                        "servicePages:part-load-booking.difference-between-part-load-and-full-load-transport.category-1"
                                    )}
                                </Typography>
                                <GenericTitleDescriptionWithParas heading={""} titleDescriptionList={usePlTPros()} />
                            </div>
                            <div>
                                <Typography as="p">
                                    {t(
                                        "servicePages:part-load-booking.difference-between-part-load-and-full-load-transport.category-2"
                                    )}
                                </Typography>
                                <GenericTitleDescriptionWithParas heading={""} titleDescriptionList={useFlTPros()} />
                            </div>
                        </Flex>
                    }
                />

                <Features
                    features={usePartLoadFeatures()}
                    sectionTitle={t("servicePages:part-load-booking.features.sectionTitle")}
                    ctaLink={`${ROUTES.marketplace.getUrl("load")}`}
                    ctaLabel={t("servicePages:part-load-booking.features.ctaLabel")}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_part_truck_load, {
                            value: GA4ServicePagePartTruckLoadEventValues.explore_part_load_market
                        });
                    }}
                />

                <HowItWorks
                    howitwork={useOlbHowItworkData()}
                    sectionTitle={t("servicePages:part-load-booking.how-it-works.section-title")}
                    sectionText={t("servicePages:part-load-booking.how-it-works.section-text")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_part_truck_load, {
                            value: GA4ServicePagePartTruckLoadEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_part_truck_load, {
                            value: GA4ServicePagePartTruckLoadEventValues.web
                        });
                    }}
                />

                <Faq
                    faqs={faqs}
                    sectionTitle={t("servicePages:part-load-booking.faq.sectionTitle")}
                    sectionText={t("servicePages:part-load-booking.faq.sectionText")}
                />

                <OurCustomerSays
                    customerSaysData={useCustomerSaysData()}
                    sectionTitle={t("servicePages:logistics-services.ourCustomerSays.sectionTitle")}
                    sectionText={t("servicePages:logistics-services.ourCustomerSays.sectionText")}
                />
            </main>
        </>
    );
};

export default PartLoadBooking;
